import { useState, useEffect } from 'react';

export const useDarkMode = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  // localStorage에서 다크모드 설정 불러오기
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    if (savedDarkMode) {
      setIsDarkMode(savedDarkMode === 'true');
    }
  }, []);

  // localStorage 변화 감지
  useEffect(() => {
    const handleStorageChange = () => {
      const savedDarkMode = localStorage.getItem('darkMode');
      if (savedDarkMode) {
        setIsDarkMode(savedDarkMode === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    const interval = setInterval(() => {
      const savedDarkMode = localStorage.getItem('darkMode');
      if (savedDarkMode && savedDarkMode !== isDarkMode.toString()) {
        setIsDarkMode(savedDarkMode === 'true');
      }
    }, 100);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [isDarkMode]);

  return isDarkMode;
};

// Clozet 디자인 가이드에 맞는 스타일 객체
export const getClozedStyles = (isDarkMode: boolean) => ({
  // 페이지 컨테이너
  pageContainer: {
    bgcolor: 'transparent',
    minHeight: '100vh',
  },
  
  // 제목 텍스트
  title: {
    color: isDarkMode ? '#fff' : '#000',
  },
  
  // 카드/Paper 스타일
  paper: {
    bgcolor: isDarkMode ? '#2d2d2d' : '#ffffff',
    border: isDarkMode ? '1px solid #404040' : '1px solid #e0e0e0',
    borderRadius: 3,
    boxShadow: isDarkMode ? '0px 2px 4px rgba(0, 0, 0, 0.3)' : '0px 2px 4px rgba(0, 0, 0, 0.05)',
    transition: 'background-color 0.3s ease, border-color 0.3s ease',
  },
  
  // 텍스트 색상
  text: {
    color: isDarkMode ? '#fff' : '#000',
    primary: isDarkMode ? '#fff' : '#000',
    secondary: isDarkMode ? '#ccc' : '#666',
  },

  textSecondary: {
    color: isDarkMode ? '#ccc' : '#666',
  },
  
  // 버튼 스타일
  button: {
    primary: {
      bgcolor: isDarkMode ? '#fff' : '#000',
      color: isDarkMode ? '#000' : '#fff',
      '&:hover': {
        bgcolor: isDarkMode ? '#f0f0f0' : '#333',
      },
    },
  },
  
  // 테이블 관련
  table: {
    header: {
      bgcolor: isDarkMode ? '#404040' : '#f5f5f5',
      color: isDarkMode ? '#fff' : '#000',
    },
    cell: {
      color: isDarkMode ? '#fff' : '#000',
      borderColor: isDarkMode ? '#404040' : '#e0e0e0',
    },
  },

  tableCell: {
    color: isDarkMode ? '#fff !important' : '#000 !important',
    borderColor: isDarkMode ? '#404040' : '#e0e0e0',
  },

  tableContainer: {
    '& .MuiTableCell-root': {
      color: isDarkMode ? '#fff' : '#000',
      borderColor: isDarkMode ? '#404040' : '#e0e0e0',
    },
    '& .MuiTableCell-head': {
      color: isDarkMode ? '#fff' : '#000',
      fontWeight: 600,
    },
  },
  
  // 입력 필드
  input: {
    '& .MuiOutlinedInput-root': {
      bgcolor: isDarkMode ? '#404040' : '#ffffff',
      color: isDarkMode ? '#fff' : '#000',
      '& fieldset': {
        borderColor: isDarkMode ? '#606060' : '#e0e0e0',
      },
      '&:hover fieldset': {
        borderColor: isDarkMode ? '#808080' : '#c0c0c0',
      },
      '&.Mui-focused fieldset': {
        borderColor: isDarkMode ? '#fff' : '#000',
      },
    },
    '& .MuiInputLabel-root': {
      color: isDarkMode ? '#ccc' : '#666',
      '&.Mui-focused': {
        color: isDarkMode ? '#fff' : '#000',
      },
    },
    '& .MuiSelect-select': {
      color: isDarkMode ? '#fff' : '#000',
    },
    '& .MuiMenuItem-root': {
      color: isDarkMode ? '#fff' : '#000',
    },
  },

  // MenuItem 스타일 (Popper/Menu 내부)
  menuItem: {
    color: isDarkMode ? '#fff' : '#000',
    bgcolor: isDarkMode ? '#2d2d2d' : '#ffffff',
    '&:hover': {
      bgcolor: isDarkMode ? '#404040' : '#f5f5f5',
    },
    '&.Mui-selected': {
      bgcolor: isDarkMode ? '#404040' : '#e8e8e8',
      '&:hover': {
        bgcolor: isDarkMode ? '#505050' : '#d8d8d8',
      },
    },
  },

  // Select dropdown 메뉴
  selectMenu: {
    '& .MuiPaper-root': {
      bgcolor: isDarkMode ? '#2d2d2d' : '#ffffff',
      border: isDarkMode ? '1px solid #404040' : '1px solid #e0e0e0',
    },
    '& .MuiMenuItem-root': {
      color: isDarkMode ? '#fff' : '#000',
      '&:hover': {
        bgcolor: isDarkMode ? '#404040' : '#f5f5f5',
      },
      '&.Mui-selected': {
        bgcolor: isDarkMode ? '#404040' : '#e8e8e8',
        '&:hover': {
          bgcolor: isDarkMode ? '#505050' : '#d8d8d8',
        },
      },
    },
  },
});